<template>
  <div class="support-launcher">
    <button
      type="button"
      class="support-launcher__button"
      aria-label="Open support conversations"
      @click="openInbox()"
    >
      <span class="support-launcher__icon" aria-hidden="true">?</span>
      <span class="support-launcher__tooltip" role="tooltip">Support</span>
      <strong v-if="unread" :aria-label="`${unread} unread support tickets`">{{ displayUnread }}</strong>
    </button>
    <support-ticket-inbox-modal
      :initial-ticket-id="initialTicketId"
      @create="openCreate"
      @navigate="goToMyTickets"
      @read="loadBadge"
      @updated="loadBadge"
    />
    <ticket-create-modal @created="ticketCreated" />
  </div>
</template>

<script>
import { getSupportTickets } from '@/http'
import SupportTicketInboxModal from './SupportTicketInboxModal'
import TicketCreateModal from './TicketCreateModal'

export default {
  name: 'SupportTicketLauncher',
  components: { SupportTicketInboxModal, TicketCreateModal },
  data() { return { unread: 0, initialTicketId: null } },
  computed: {
    displayUnread() { return this.unread > 99 ? '99+' : this.unread },
    currentUserId() { return Number(this.$store.state.userData?.id) || null }
  },
  created() {
    this.loadBadge()
    this.$root.$on('support-ticket-refresh', this.loadBadge)
  },
  beforeDestroy() {
    this.$root.$off('support-ticket-refresh', this.loadBadge)
  },
  methods: {
    loadBadge() {
      if (!this.currentUserId) return
      getSupportTickets({ requester: this.currentUserId, unread: true, page_size: 1 })
        .then(response => { this.unread = response.data.count || 0 })
        .catch(() => {})
    },
    openInbox(ticketId = null) {
      this.initialTicketId = ticketId ? Number(ticketId) : null
      this.$nextTick(() => this.$modal.show('support-ticket-inbox'))
    },
    openCreate() {
      this.$nextTick(() => this.$modal.show('support-ticket-create'))
    },
    goToMyTickets(ticketId) {
      const query = ticketId ? { ticket: ticketId } : {}
      this.$router.push({ name: 'support-ticket-my-tickets', query }).catch(() => {})
    },
    ticketCreated(ticket) {
      this.loadBadge()
      this.$root.$emit('support-ticket-refresh')
      this.openInbox(ticket.id)
    }
  }
}
</script>

<style lang="scss" scoped>
// Keep dialogs outside the floating button's stacking context, above the AI panel.
.support-launcher :deep(.support-create-modal),
.support-launcher :deep(.support-inbox-modal) { z-index: 10040; }
.support-launcher__button {
  position: fixed;
  z-index: 60;
  right: 20px;
  bottom: 20px;
  display: inline-flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, .24);
  border-radius: 50%;
  background: rgba(15, 23, 42, .62);
  box-shadow: 0 8px 20px rgba(15, 23, 42, .16);
  backdrop-filter: blur(8px);
  color: $ui-surface;
  cursor: pointer;
  transition: background-color .18s ease, box-shadow .18s ease, transform .18s ease;
}
.support-launcher__button:hover { background: rgba(37, 99, 235, .82); box-shadow: 0 10px 24px rgba(15, 23, 42, .22); transform: translateY(-1px); }
.support-launcher__button:focus-visible { outline: 3px solid rgba($ui-blue, .32); outline-offset: 3px; }
.support-launcher__icon {
  display: grid;
  width: 100%;
  height: 100%;
  place-items: center;
  color: rgba(255, 255, 255, .94);
  font: 700 19px $ui-font-heading;
}
.support-launcher__tooltip {
  position: absolute;
  top: 50%;
  right: calc(100% + 10px);
  width: max-content;
  padding: 6px 9px;
  border-radius: 7px;
  background: rgba(15, 23, 42, .88);
  color: #fff;
  font: 600 11px $ui-font-body;
  opacity: 0;
  pointer-events: none;
  transform: translate(4px, -50%);
  transition: opacity .15s ease, transform .15s ease;
}
.support-launcher__button:hover .support-launcher__tooltip,
.support-launcher__button:focus-visible .support-launcher__tooltip {
  opacity: 1;
  transform: translate(0, -50%);
}
.support-launcher strong {
  position: absolute;
  top: -5px;
  right: -5px;
  display: grid;
  min-width: 18px;
  height: 18px;
  place-items: center;
  padding: 0 4px;
  border: 2px solid $ui-surface;
  border-radius: 10px;
  background: $ui-red;
  color: #fff;
  font: 700 9px $ui-font-body;
}
@media (max-width: 760px) {
  .support-launcher__button { right: 12px; bottom: 12px; width: 36px; height: 36px; background: rgba(15, 23, 42, .54); }
  .support-launcher__icon { font-size: 17px; }
  .support-launcher__tooltip { display: none; }
  .support-launcher strong { top: -6px; right: -6px; }
}
</style>
